import type { ReactElement } from 'react';

/**
 * One stroke weight, one 24-grid, one visual voice.
 *
 * Drawn rather than pulled from an icon set so the vocabulary is the app's own: the search
 * glyph is a coach, not a magnifying glass, because this app does not search the web — it
 * searches for a bus.
 */

const BOX = {
  viewBox: '0 0 24 24',
  fill: 'none',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export type GlyphName =
  | 'search'
  | 'bookings'
  | 'travellers'
  | 'profile'
  | 'offers'
  | 'help'
  | 'about'
  | 'menu'
  | 'close'
  | 'chevron'
  | 'signIn'
  | 'signOut'
  | 'globe'
  | 'check';

const PATHS: Record<GlyphName, ReactElement> = {
  // A coach: this app searches for buses.
  search: (
    <>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M3 10h18M7 16v2M17 16v2" />
      <circle cx="7.5" cy="19" r="1.5" />
      <circle cx="16.5" cy="19" r="1.5" />
    </>
  ),
  // A ticket with its perforation — the same stub the ticket screen draws.
  bookings: (
    <>
      <path d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 0 0 6v3a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-6z" />
      <path d="M12 8v8" strokeDasharray="2 3" />
    </>
  ),
  // Two people: the passenger plus whoever they book for.
  travellers: (
    <>
      <path d="M16 19v-1.5a3.5 3.5 0 0 0-3.5-3.5h-4A3.5 3.5 0 0 0 5 17.5V19" />
      <circle cx="10.5" cy="8" r="3" />
      <path d="M19 19v-1.5a3.5 3.5 0 0 0-2.6-3.4M15.5 5.2a3 3 0 0 1 0 5.6" />
    </>
  ),
  // One person: the account holder, not the list of people they book for.
  profile: (
    <>
      <path d="M19 20v-1.5a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4V20" />
      <circle cx="12" cy="7.5" r="3.5" />
    </>
  ),
  offers: (
    <>
      <path d="M3.5 11.2V4.5a1 1 0 0 1 1-1h6.7a1 1 0 0 1 .7.3l8 8a1 1 0 0 1 0 1.4l-6.7 6.7a1 1 0 0 1-1.4 0l-8-8a1 1 0 0 1-.3-.7z" />
      <circle cx="8" cy="8" r="1.4" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.2 2.4c-.6.2-.7.7-.7 1.3" />
      <path d="M12 16.5h.01" />
    </>
  ),
  about: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3.2 9.5h17.6M3.2 14.5h17.6" />
      <path d="M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18z" />
    </>
  ),
  check: <path d="M4.5 12.5l5 5 10-10" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  chevron: <path d="M9 5l7 7-7 7" />,
  signIn: (
    <>
      <path d="M15 3h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-3" />
      <path d="M10 17l5-5-5-5M15 12H3" />
    </>
  ),
  signOut: (
    <>
      <path d="M9 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3" />
      <path d="M16 17l5-5-5-5M21 12H9" />
    </>
  ),
};

export function Glyph({
  name,
  size = 22,
  active = false,
  className,
}: {
  name: string;
  size?: number;
  /** The active item is drawn heavier as well as recoloured — weight reads before hue does. */
  active?: boolean;
  className?: string;
}) {
  const path = PATHS[name as GlyphName] ?? PATHS.about;
  return (
    <svg
      {...BOX}
      width={size}
      height={size}
      stroke="currentColor"
      strokeWidth={active ? 2 : 1.6}
      className={className}
      aria-hidden
    >
      {path}
    </svg>
  );
}
